/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import SpacerLogKpiCard from './SpacerLogKpiCard';
import SpacerLogKpiStrip from './SpacerLogKpiStrip';
import {
  fmtMinShort,
  cycleTimeBarStats,
  CHART_COLORS,
  BRAND_TEAL,
  BRAND_TEAL_DIM,
} from './spacerLogUtils';
import { baseChartOptions, gridScales } from './chartSetup';
import './chartSetup';

const STAGE_LABELS = {
  fitup: 'Fit-up',
  stn1Weld: 'S1 Weld',
  lastSt: 'Last ST',
  pull: 'Pull',
};

const STAGE_COLORS = [BRAND_TEAL, BRAND_TEAL_DIM, '#2E3A4A', '#7C8A9C'];

const SpacerLogShiftOverviewView = ({ data, isLoading = false }) => {
  const { kpis, charts } = data || {};

  const kpiStripContent = useMemo(() => {
    if (!kpis) return null;
    const incomplete = kpis.incomplete ?? 0;
    const completePct = kpis.completePct ?? 0;
    const bn = kpis.bottleneck;

    return (
      <>
        <SpacerLogKpiCard label="Welds" value={kpis.welds ?? 0} sub="logged this shift" rail="neutral" />
        <SpacerLogKpiCard
          label="Valid Cycles"
          value={kpis.validCycles ?? 0}
          sub="used in chart"
          rail="good"
        />
        <SpacerLogKpiCard
          label="Anomalies"
          value={kpis.anomalies ?? 0}
          sub="timestamp issues"
          rail={kpis.anomalies > 0 ? 'warn' : 'good'}
        />
        <SpacerLogKpiCard
          label="Incomplete"
          value={incomplete}
          sub="missing fields"
          rail={incomplete > 0 ? 'bad' : 'good'}
        />
        <SpacerLogKpiCard
          label="Complete"
          value={`${completePct}%`}
          sub="records with all fields"
          rail={completePct >= 90 ? 'good' : completePct >= 75 ? 'warn' : 'bad'}
        />
        <SpacerLogKpiCard
          label="Throughput"
          value={`${kpis.throughputPerHour ?? '—'} /h`}
          sub="joints per hour"
          rail="neutral"
        />
        <SpacerLogKpiCard
          label="Shift Span"
          value={kpis.shiftSpanLabel || fmtMinShort(kpis.shiftSpanMinutes)}
          sub="pipe in to last finish"
          rail="neutral"
        />
        <SpacerLogKpiCard
          label="Bottleneck"
          value={bn?.minutes != null ? fmtMinShort(bn.minutes) : '—'}
          sub={bn?.stage || 'slowest stage'}
          rail="warn"
        />
      </>
    );
  }, [kpis]);

  const cycleAllWelds = charts?.cycleTimeAllWelds;
  const barStats = useMemo(
    () => cycleTimeBarStats(cycleAllWelds?.bars || []),
    [cycleAllWelds?.bars]
  );

  const cycleTimeChart = useMemo(() => {
    const bars = cycleAllWelds?.bars || [];
    if (!bars.length) return null;

    const labels = bars.map((b) => `#${b.jointId}`);
    const values = bars.map((b) => b.cycleTimeMinutes);
    const n = labels.length;
    const mean = cycleAllWelds.meanMinutes;
    const median = cycleAllWelds.medianMinutes;
    const mode = cycleAllWelds.modeMinutes;

    const makeConstLine = (val, label, color, dash) => ({
      type: 'line',
      label,
      data: Array(n).fill(val),
      borderColor: color,
      borderWidth: 1.5,
      borderDash: dash,
      pointRadius: 0,
      fill: false,
      order: 0,
    });

    const datasets = [
      {
        type: 'bar',
        label: 'Cycle time',
        data: values,
        backgroundColor: 'rgba(0, 168, 142, 0.55)',
        borderColor: BRAND_TEAL,
        borderWidth: 1,
        borderRadius: 2,
        maxBarThickness: 28,
        order: 1,
      },
    ];

    if (mean != null) datasets.push(makeConstLine(mean, `Mean ${fmtMinShort(mean)}`, '#C4001F', [6, 4]));
    if (median != null) datasets.push(makeConstLine(median, `Median ${fmtMinShort(median)}`, '#2E3A4A', [4, 4]));
    if (mode != null) datasets.push(makeConstLine(mode, `Mode ${fmtMinShort(mode)}`, '#8695A1', [2, 4]));

    return {
      data: { labels, datasets },
      options: {
        ...baseChartOptions,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'top',
            align: 'start',
            labels: { boxWidth: 14, boxHeight: 2, color: CHART_COLORS.text, usePointStyle: false },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                if (ctx.dataset.type === 'bar') return ` ${fmtMinShort(ctx.parsed.y)}`;
                return ` ${ctx.dataset.label}`;
              },
            },
          },
        },
        scales: {
          ...gridScales,
          x: { ...gridScales.x, ticks: { ...gridScales.x.ticks, maxRotation: 45, minRotation: 0 } },
        },
      },
    };
  }, [cycleAllWelds]);

  const cycleBands = charts?.cycleTimeBands || [];
  const maxBandCount = Math.max(...cycleBands.map((b) => b.count || 0), 1);

  const vsMean = charts?.vsShiftMean;
  const vsMeanTotal = (vsMean?.below ?? 0) + (vsMean?.onMark ?? 0) + (vsMean?.above ?? 0);

  const meanStage = charts?.meanStageTime;
  const stageEntries = meanStage
    ? [
        { key: 'fitup', minutes: meanStage.fitup },
        { key: 'stn1Weld', minutes: meanStage.stn1Weld },
        { key: 'lastSt', minutes: meanStage.lastSt },
        { key: 'pull', minutes: meanStage.pull },
      ].filter((s) => s.minutes != null)
    : [];
  const stageTotal = stageEntries.reduce((sum, s) => sum + (s.minutes || 0), 0);

  const stationMix = charts?.lastStationMix || [];
  const maxStationCount = Math.max(...stationMix.map((s) => s.count || 0), 1);

  const recordQuality = charts?.recordQuality || [];

  return (
    <div className="spacer-log-shift-overview">
      {kpiStripContent || isLoading ? (
        <SpacerLogKpiStrip isLoading={isLoading && !kpis}>{kpiStripContent}</SpacerLogKpiStrip>
      ) : null}

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">Cycle time · all welds</span>
          <span className="spacer-log-panel__note">
            Each bar is that weld&apos;s cycle time. Dashed lines are shift mean, median, and mode.
          </span>
        </div>
        <div className="spacer-log-chart-box spacer-log-chart-box--tall">
          {cycleTimeChart ? <Chart type="bar" data={cycleTimeChart.data} options={cycleTimeChart.options} /> : null}
        </div>
        {barStats ? (
          <div className="spacer-log-cycle-stats">
            {[
              ['Fastest', barStats.fastest],
              ['Slowest', barStats.slowest],
              ['Spread', barStats.spread],
              ['P25', barStats.p25],
              ['P75', barStats.p75],
              ['P90', barStats.p90],
            ].map(([label, val]) => (
              <div key={label} className="spacer-log-cycle-stat">
                <span className="spacer-log-cycle-stat__label">{label}</span>
                <span className="spacer-log-cycle-stat__value">{fmtMinShort(val)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="spacer-log-row2">
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Cycle time bands</span>
            <span className="spacer-log-panel__note">distribution of cycle durations</span>
          </div>
          <div className="spacer-log-hbar-list">
            {cycleBands.length ? (
              cycleBands.map((band) => (
                <div key={band.label} className="spacer-log-hbar-row">
                  <span className="spacer-log-hbar-label">{band.label}</span>
                  <div className="spacer-log-hbar-track">
                    <div
                      className="spacer-log-hbar-fill"
                      style={{ width: `${((band.count || 0) / maxBandCount) * 100}%` }}
                    />
                  </div>
                  <span className="spacer-log-hbar-meta">
                    {band.count ?? 0} · {band.pct ?? 0}%
                  </span>
                </div>
              ))
            ) : (
              <p className="spacer-log-empty-note">No cycle time band data for this shift.</p>
            )}
          </div>
        </div>

        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Vs shift mean</span>
            <span className="spacer-log-panel__note">valid cycles compared to shift average</span>
          </div>
          {vsMeanTotal > 0 ? (
            <>
              <div className="spacer-log-vs-mean-bar">
                <div
                  className="spacer-log-vs-mean-seg spacer-log-vs-mean-seg--below"
                  style={{ flex: vsMean.below || 0 }}
                />
                <div
                  className="spacer-log-vs-mean-seg spacer-log-vs-mean-seg--on"
                  style={{ flex: vsMean.onMark || 0 }}
                />
                <div
                  className="spacer-log-vs-mean-seg spacer-log-vs-mean-seg--above"
                  style={{ flex: vsMean.above || 0 }}
                />
              </div>
              <div className="spacer-log-vs-mean-legend">
                <span><i className="spacer-log-vs-mean-dot spacer-log-vs-mean-dot--below" />{vsMean.below ?? 0} below</span>
                <span><i className="spacer-log-vs-mean-dot spacer-log-vs-mean-dot--on" />{vsMean.onMark ?? 0} on mark</span>
                <span><i className="spacer-log-vs-mean-dot spacer-log-vs-mean-dot--above" />{vsMean.above ?? 0} above</span>
              </div>
            </>
          ) : (
            <p className="spacer-log-empty-note">No shift-mean comparison data.</p>
          )}
        </div>
      </div>

      <div className="spacer-log-row2">
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Mean stage time</span>
            <span className="spacer-log-panel__note">avg minutes per stage</span>
          </div>
          {stageEntries.length ? (
            <>
              <div className="spacer-log-stage-bar">
                {stageEntries.map((s, i) => (
                  <div
                    key={s.key}
                    className="spacer-log-stage-seg"
                    style={{
                      flex: s.minutes || 0,
                      background: STAGE_COLORS[i % STAGE_COLORS.length],
                    }}
                    title={`${STAGE_LABELS[s.key] || s.key}: ${fmtMinShort(s.minutes)}`}
                  />
                ))}
              </div>
              <div className="spacer-log-stage-legend">
                {stageEntries.map((s, i) => (
                  <div key={s.key} className="spacer-log-stage-legend-item">
                    <span
                      className="spacer-log-stage-swatch"
                      style={{ background: STAGE_COLORS[i % STAGE_COLORS.length] }}
                    />
                    <span className="spacer-log-stage-name">{STAGE_LABELS[s.key] || s.key}</span>
                    <span className="spacer-log-stage-val">{fmtMinShort(s.minutes)}</span>
                  </div>
                ))}
              </div>
              {stageTotal > 0 ? (
                <p className="spacer-log-panel__note" style={{ marginTop: 8 }}>
                  Total avg cycle: {fmtMinShort(stageTotal)}
                </p>
              ) : null}
            </>
          ) : (
            <p className="spacer-log-empty-note">No stage duration data.</p>
          )}
        </div>

        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Last station mix</span>
            <span className="spacer-log-panel__note">where joints finished before vessel pull</span>
          </div>
          <div className="spacer-log-hbar-list">
            {stationMix.length ? (
              stationMix.map((row) => (
                <div key={row.station} className="spacer-log-hbar-row">
                  <span className="spacer-log-hbar-label">{row.station}</span>
                  <div className="spacer-log-hbar-track">
                    <div
                      className="spacer-log-hbar-fill"
                      style={{ width: `${((row.count || 0) / maxStationCount) * 100}%` }}
                    />
                  </div>
                  <span className="spacer-log-hbar-meta">
                    {row.count ?? 0} · {row.pct ?? 0}% · avg {fmtMinShort(row.avgCycleMinutes)}
                  </span>
                </div>
              ))
            ) : (
              <p className="spacer-log-empty-note">No station mix data.</p>
            )}
          </div>
        </div>
      </div>

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">Record quality</span>
          <span className="spacer-log-panel__note">data gaps flagged vs normal production notes</span>
        </div>
        <div className="spacer-log-quality-list">
          {recordQuality.length ? (
            recordQuality.map((row) => (
              <div key={row.label} className="spacer-log-quality-row">
                <span className="spacer-log-quality-label">{row.label}</span>
                <span
                  className={`spacer-log-quality-count${row.count > 0 ? ' spacer-log-quality-count--flagged' : ''}`}
                >
                  {row.count ?? 0}
                </span>
              </div>
            ))
          ) : (
            <p className="spacer-log-empty-note">No record quality flags.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpacerLogShiftOverviewView;
